/**
 * StepDots — onboarding step indicator (STORY-000-006a; cumulative-fill
 * corrected in STORY-000-006b).
 *
 * A horizontal row of dots used in multi-step flows
 * (docs/GardEn/src/screens/Onboarding.jsx `OnboardingShell`). Every dot
 * from the first step through `current` (inclusive) fills with
 * `bg-primary` — a **cumulative** fill that shows the progress the user
 * has made, not just where they are right now. Remaining dots use
 * `bg-muted`. The dot for `current` is also rendered wider (`w-5`) to
 * echo the prototype, where only the active step's dot is elongated.
 *
 * This matches the canonical visual reference
 * (`docs/GardEn/src/screens/Onboarding.jsx`), whose `StepDots` fills
 * `i <= current` (0-indexed). STORY-000-006a originally shipped
 * single-fill per its AC #7; that was an authoring miss against the
 * prototype, corrected here per the "Design source of truth" rule
 * (prototype wins on visual behavior).
 *
 * The wrapper exposes ARIA progressbar semantics (`role="progressbar"`
 * with `aria-valuenow`/`aria-valuemin`/`aria-valuemax`/`aria-valuetext`)
 * so screen readers announce "Step N of total".
 */
import type { ReactElement } from "react";

export interface StepDotsProps {
  /**
   * 1-indexed current step. `aria-valuenow` reflects this. Dots at
   * positions `1..current` are filled. A value of `0` (no step started
   * yet) is a valid defensive input: zero dots fill.
   */
  current: number;
  /** Total number of steps. `aria-valuemax` reflects this. */
  total: number;
  /** Additional classes for the wrapper (`role="progressbar"`). */
  className?: string;
}

/**
 * Renders `total` dots. Dots at 1-indexed positions `1..current`
 * (cumulative) are filled with `bg-primary`; later dots use `bg-muted`.
 * The dot at position `current` is widened (`w-5`) to mark the active
 * step.
 *
 * @throws never — out-of-range inputs render best-effort. At
 *   `current=0` no dots fill (pre-flow state); a `current` greater than
 *   `total` simply fills every dot. ARIA attributes are emitted
 *   as-passed so callers can debug.
 */
export function StepDots({
  current,
  total,
  className,
}: StepDotsProps): ReactElement {
  const dots = Array.from({ length: total }, (_, i) => i + 1);
  const classes = ["inline-flex", "items-center", "gap-1.5", className]
    .filter(Boolean)
    .join(" ");

  return (
    <div
      role="progressbar"
      aria-valuenow={current}
      aria-valuemin={0}
      aria-valuemax={total}
      aria-valuetext={`Step ${current} of ${total}`}
      data-testid="step-dots"
      className={classes}
    >
      {dots.map((stepIndex) => {
        // Cumulative fill: every dot up to and including `current`.
        const isFilled = stepIndex <= current;
        // The active step's dot is rendered wider, matching the prototype.
        const isCurrent = stepIndex === current;
        const dotClasses = [
          "block",
          "h-1.5",
          "rounded-full",
          "transition-all",
          "duration-200",
          isFilled ? "bg-primary" : "bg-muted",
          isCurrent ? "w-5" : "w-1.5",
        ].join(" ");
        return (
          <span
            key={stepIndex}
            data-step={stepIndex}
            data-filled={isFilled ? "true" : "false"}
            data-current={isCurrent ? "true" : "false"}
            aria-hidden="true"
            className={dotClasses}
          />
        );
      })}
    </div>
  );
}
